import React from 'react';
import { Button } from 'components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import { ChevronDown, ChevronUp, ChevronsUpDown, ArrowLeft } from 'lucide-react';
import { cn } from 'lib/utils';
import { Skeleton } from 'components/ui/skeleton';
import { useSmoothLoading } from 'lib/use-smooth-loading';

export interface OuTableRow {
  ou_code: string;
  ou_name: string;
  income: number;
  expenses: number;
  profit: number;
  profit_pct: number;
}

interface AnalysisTableProps {
  data: OuTableRow[];
  total: number;
  loading: boolean;
  conversionType: 'INR' | 'Lakhs' | 'Crores';
  onConversionChange: (type: 'INR' | 'Lakhs' | 'Crores') => void;
  sortBy: string;
  sortDir: 'asc' | 'desc';
  onSort: (by: string, dir: 'asc' | 'desc') => void;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onClose?: () => void;
  bucketTitle?: string;
  isOuView?: boolean;
  hasLoadedOnce?: boolean;
}

const AnalysisTable: React.FC<AnalysisTableProps> = ({
  data,
  total,
  loading,
  conversionType,
  onConversionChange,
  sortBy,
  sortDir,
  onSort,
  page,
  pageSize,
  onPageChange,
  onClose,
  bucketTitle,
  isOuView = false,
  hasLoadedOnce = false,
}) => {
  const { isLoading, showContent } = useSmoothLoading(loading, {
    minLoadingTime: 150,
    debounceTime: 100
  });

  const formatNumber = (value: number, isPercent = false): string => {
    if (value === 0) return isPercent ? '0.00%' : '0.00';

    let displayValue = value;
    if (!isPercent) {
      switch (conversionType) {
        case 'Lakhs':
          displayValue = value / 100000;
          break;
        case 'Crores':
          displayValue = value / 10000000;
          break;
        case 'INR':
        default:
          displayValue = value;
          break;
      }
    }

    const formatted = displayValue.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    return isPercent ? `${formatted}%` : formatted;
  };

  const handleSort = (column: string) => {
    if (sortBy === column) {
      onSort(column, sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      onSort(column, 'asc');
    }
  };

  const getSortButton = (column: string, label: string) => {
    const isActive = sortBy === column;
    const isAsc = isActive && sortDir === 'asc';
    const isDesc = isActive && sortDir === 'desc';

    return (
      <Button
        variant="ghost"
        size="sm"
        className={cn(
          "-ml-3 h-8 data-[state=open]:bg-accent",
          "hover:bg-accent hover:text-accent-foreground"
        )}
        onClick={() => handleSort(column)}
      >
        <span>{label}</span>
        {isDesc ? (
          <ChevronDown className="ml-2 h-4 w-4" />
        ) : isAsc ? (
          <ChevronUp className="ml-2 h-4 w-4" />
        ) : (
          <ChevronsUpDown className="ml-2 h-4 w-4" />
        )}
      </Button>
    );
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="space-y-4 mx-auto w-full max-w-screen-2xl 2xl:max-w-[2000px]">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h3 className="text-md font-semibold">
            {`OU Details${bucketTitle && ` - ${bucketTitle}`}`}
          </h3>
        </div>
        <div>
          <div className="flex items-center justify-between">
            <div className="flex gap-2">
              <button
                className={`px-3 py-1 text-sm rounded-md transition-colors ${conversionType === 'INR'
                  ? 'bg-black text-white'
                  : 'bg-white border border-black hover:bg-gray-200'
                  }`}
                onClick={() => onConversionChange('INR')}
              >
                INR
              </button>
              <button
                className={`px-3 py-1 text-sm rounded-md transition-colors ${conversionType === 'Lakhs'
                  ? 'bg-black text-white'
                  : 'bg-white border border-black hover:bg-gray-200'
                  }`}
                onClick={() => onConversionChange('Lakhs')}
              >
                Lakhs
              </button>
              <button
                className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${conversionType === 'Crores'
                  ? 'bg-black text-white'
                  : 'bg-white border border-black hover:bg-gray-200'
                  }`}
                onClick={() => onConversionChange('Crores')}
              >
                Crores
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[400px]">
                {getSortButton('ou_name', 'OU Name')}
              </TableHead>
              <TableHead className="text-right">
                {getSortButton('income', 'Income')}
              </TableHead>
              <TableHead className="text-right">
                {getSortButton('expenses', 'Expenses')}
              </TableHead>
              <TableHead className="text-right">
                {getSortButton('profit', 'Profit')}
              </TableHead>
              <TableHead className="text-right">
                {getSortButton('profit_pct', 'Profit %')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              // Skeleton rows for loading state
              Array.from({ length: Math.min(pageSize, 10) }).map((_, index) => (
                <TableRow key={`skeleton-${index}`}>
                  <TableCell>
                    <Skeleton className="h-4 w-3/4" />
                  </TableCell>
                  <TableCell className="text-right">
                    <Skeleton className="h-4 w-20 ml-auto" />
                  </TableCell>
                  <TableCell className="text-right">
                    <Skeleton className="h-4 w-20 ml-auto" />
                  </TableCell>
                  <TableCell className="text-right">
                    <Skeleton className="h-4 w-16 ml-auto" />
                  </TableCell>
                  <TableCell className="text-right">
                    <Skeleton className="h-4 w-12 ml-auto" />
                  </TableCell>
                </TableRow>
              ))
            ) : data.length === 0 && hasLoadedOnce ? (
              <TableRow>
                <TableCell colSpan={5} className="h-24 text-center">
                  <div className="flex flex-col items-center justify-center text-muted-foreground">
                    <p className="text-sm">No OU data available</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              data.map((row) => (
                <TableRow
                  key={row.ou_code}
                  className={cn(
                    `transition-opacity duration-300 ${showContent ? 'opacity-100' : 'opacity-0'}`
                  )}
                >
                  <TableCell>
                    <div className="font-medium">
                      {row.ou_name.trim()}
                    </div>
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {formatNumber(row.income)}
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {formatNumber(row.expenses)}
                  </TableCell>
                  <TableCell className={cn(
                    "text-right font-medium",
                    row.profit >= 0 ? "text-green-700" : "text-red-700"
                  )}>
                    {formatNumber(row.profit)}
                  </TableCell>
                  <TableCell className={cn(
                    "text-right font-medium",
                    row.profit_pct >= 0 ? "text-green-700" : "text-red-700"
                  )}>
                    {formatNumber(row.profit_pct, true)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
          >
            Previous
          </Button>

          {/* Page numbers */}
          {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
            let pageNum;
            if (totalPages <= 5) {
              pageNum = i + 1;
            } else if (page <= 3) {
              pageNum = i + 1;
            } else if (page >= totalPages - 2) {
              pageNum = totalPages - 4 + i;
            } else {
              pageNum = page - 2 + i;
            }

            return (
              <Button
                key={pageNum}
                variant={page === pageNum ? "default" : "outline"}
                size="sm"
                onClick={() => onPageChange(pageNum)}
              >
                {pageNum}
              </Button>
            );
          })}

          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(page + 1)}
            disabled={page >= totalPages}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
};

export default AnalysisTable;
